import React from 'react'
import { Col, Button, Container } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { getQueryParams } from '../../utils/helpers'
import { DefaultSpinner } from '../pageBlocks/Spinners'
import httpClient from '../../services/httpClient'
import { appInsights } from '../../appInsights'
import * as webeid from '../../utils/web-eid/web-eid'

interface ICardIdAuthProps {
    id: string
    onError(error: Error | undefined): void
    onSuccess(str: string): void
}

function IdCardContentBlock({ id, onError }: ICardIdAuthProps) {
    const { t, i18n } = useTranslation()
    const [isWaiting, setIsWaiting] = React.useState(false)

    async function tryIdCardAuth() {
        onError(undefined)
        setIsWaiting(true)

        try {
            const { ReturnUrl, SigningRequestId } = getQueryParams()

            const returnUrl =
                ReturnUrl ||
                `${window.location.protocol}//${window.location.host}/sign/${
                    id ? id : ''
                }`

            const nonce = await httpClient.idCardAuthChallenge()

            const authToken = await webeid.authenticate(nonce, {
                lang: i18n.language.substring(0, 2),
            })

            const { redirectUrl, secret } =
                await httpClient.idCardAuthComplete({
                    authToken,
                    nonce,
                    returnUrl,
                    signingRequestId: SigningRequestId || '',
                })

            httpClient.redirectTo(redirectUrl, secret)
        } catch (exception) {
            appInsights.trackException({ exception: exception as Error })

            const { code } = exception as webeid.WebEidError
            onError(
                code
                    ? new Error(
                          t(`IdCardAuthError_${code}`, t('IdCardAuthError'))
                      )
                    : (exception as Error)
            )
            setIsWaiting(false)
        }
    }

    if (isWaiting) {
        return (
            <Container className="IdCard">
                <Col xs={12}>
                    <DefaultSpinner />
                </Col>
            </Container>
        )
    }

    return (
        <Container className="IdCard">
            <IdCardHelpText />

            <Col xs={12}>
                <Button onClick={tryIdCardAuth} variant="primary">
                    {t('Proceed')}
                </Button>
            </Col>
        </Container>
    )
}

export function IdCardHelpText() {
    const { t } = useTranslation()
    const line1 = t(
        'Before using your ID-Card, please make sure that your ID-Card is inserted into your card reader'
    )
    const line2 = t('If the login fails please check the possible solutions')
    const line3 = t('at id_ee help centre')

    return (
        <Col xs={12} className="idCardHelpText">
            <p>{line1}</p>
            <p>
                {line2}{' '}
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('IdCardHelpLink')}
                >
                    {line3}
                </a>
            </p>
        </Col>
    )
}

export default IdCardContentBlock
